import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import styles from "../index.module.css"
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import DoneIcon from '@material-ui/icons/Done';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Storage } from "aws-amplify";
import { useSelector } from 'react-redux';
import { DateTime as d } from "luxon";
import InfoIcon from '@material-ui/icons/Info';
import { IAppState } from '../../../../../store';
import { DATE_TYPE, getAddonIcon, getFeatureTypeIcon, getRetnalTaxAmount, getTaxDescription, getVehicleTypeIcon, isLongTermBooking, toCurrency } from '../../../../utils';
import { SimpleDateTimePicker } from '../../../../SimpleDateTimePicker/intex';
import { getMaxPickupDateTime, getMinPickupDateTime } from '../../../../../utils/leadTime';
import { NuvvenDivider } from '../../../Summary/Components/Divider';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, FormControl, Grid, IconButton, InputLabel, ListSubheader, makeStyles, MenuItem, Select, Tooltip, Typography, useTheme } from '@material-ui/core';
import Slider from 'react-slick';
import { BILLING_CYCLE_NAME, IAddonRequirementInput, IAddons, IBookingCreateInput, ISubscription, ISubscriptionArgs, ISubscriptionInfo, ISubscriptionPricing, ISuggestedPricing, IVehicle } from '../../../../../reducers/bookings/types';
import { useLazyQuery } from '@apollo/client';
import { GET_AVAILABLE_ADDON_TYPES } from '../../../../../graphql/addOns/getAvailableAddOnsQuery';
import { capitalizeFirstLetter, RATE_TYPES } from '../../../utils';
import FlatPickerBar from '../../../../FlatPicker';
import { getLocalizedDateFormat, getLocalizedTaxSyntex } from '../../../../../utils/localized.syntex';
import { AddonsSelection } from '../SelectAddOns/AddonsSelection';
import { DefaultAddons, IAddonRate, IAddonType } from '../SelectAddOns/types';
import { useTranslation } from 'react-i18next';
import VehicleFeatures from '../../../../VehicleFeatures/VehicleFeatures';

const useStyles = makeStyles((theme) => ({
  specBox: {
    display: "flex",
    overflowX: "auto",
    paddingBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    "&::-webkit-scrollbar": { height: 6 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: 3,
    },
    justifyContent: "flex-start",
    gap: theme.spacing(1),
  },
  specItem: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 1.5),
    border: "1px solid #eee",
    borderRadius: 12,
    backgroundColor: "#fafafa",
    "&:hover": {
      backgroundColor: "#f3f3f3",
    },
  },
  specLabel: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#111",
    marginLeft: theme.spacing(0.5),
  },
  cardContainer: {
    border: `1px solid rgba(0, 47, 110, 0.2)`,
    borderRadius: 16,
    padding: 24,
    backgroundColor: "#fff",
    boxShadow: "0 4px 12px rgba(0, 47, 110, 0.08)",
  },
  cardContainerSecondary: {
    border: `1px solid rgba(255, 172, 49, 0.2)`,
    borderRadius: 16,
    padding: 24,
    backgroundColor: "#fff",
    boxShadow: "0 4px 12px rgba(255, 172, 49, 0.08)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 20,
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  iconWrapperPrimary: {
    backgroundColor: "rgba(0, 47, 110, 0.1)",
  },
  iconWrapperSecondary: {
    backgroundColor: "rgba(255, 172, 49, 0.1)",
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: 18,
    margin: 0,
  },
  listContainer: {
    margin: 0,
    paddingLeft: 0,
    listStyle: "none",
  },
  listItem: {
    marginBottom: 14,
    display: "flex",
    alignItems: "flex-start",
  },
  listDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 12,
    marginTop: 6,
    flexShrink: 0,
  },
  listDotPrimary: {
    backgroundColor: theme.palette.primary.main,
  },
  listDotSecondary: {
    backgroundColor: theme.palette.secondary.main,
  },
  listText: {
    fontSize: 14,
    color: "#333",
    lineHeight: 1.5,
  },
}));

enum RentalTimeText {
  "hourly" = "hour",
  "daily" = "day",
  "weekly" = "week",
  "monthly" = "month"
}

interface IProps {
  bookingData: IBookingCreateInput;
  setBookingData: Dispatch<SetStateAction<IBookingCreateInput>>;
  onSubmit: (data: IBookingCreateInput) => void;
  selectedVehicle?: IVehicle;
  onSelectAvailabilityDate: (date: string) => void;
  onSelectSubscription: (selectedSubscription: ISubscriptionArgs, vehicle: IVehicle, startDate: string) => void
}

interface IDateTime {
  date: string;
  time: number
}

const settings = {
  dots: true,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  speed: 1500,
  autoplaySpeed: 3000,
  cssEase: "linear"
};
const VehicleDetail: React.FC<IProps> = (props) => {
  const classes = useStyles();
  const theme = useTheme();

  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const organisation = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.organisation);
  const branches = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.branches);
  const authState = useSelector((state: IAppState) => state.authReducer);
  const authUser = useSelector((state: IAppState) => state.authReducer.user);
  const { locale, currency } = organisation;
  const { country } = website.organisation.address;
  const { vehicleDetailsSettings } = website;
  const leadTimeSettings = {
    minLeadTimeOptIn: website.minLeadTimeOptIn,
    minLeadTimeValue: website.minLeadTimeValue,
    minLeadTimeUnit: website.minLeadTimeUnit,
    maxLeadTimeOptIn: website.maxLeadTimeOptIn,
    maxLeadTimeValue: website.maxLeadTimeValue,
    maxLeadTimeUnit: website.maxLeadTimeUnit,
  };
  const branch = branches.find((branch) => branch.id === props.bookingData.branchId);
  const maxPickupDateTime = getMaxPickupDateTime(leadTimeSettings, branch?.timeZone);
  const [selectedPlan, setSelectedPlan] = useState<ISubscriptionInfo>()
  const [selectedPricing, setSelectedPricing] = useState<ISubscriptionPricing>()
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [minDate, setMinDate] = useState<string>('')
  const [timeZone, setTimeZone] = useState<string>("")
  const { t } = useTranslation();
  const [bookingDurationInDays, setBookingDurationInDays] = useState<any>();

  const isContractHire = website.organisation.bookingCategories.some((category) => category.isContractHire === true)
  const taxes = branch && branch.addTaxOptIn ? branch.taxes.map((tax) => {
    return {
      code: tax.title,
      type: tax.type,
      rate: tax.rate,
      amount: tax.value,
      isVehicleGroupSpecific: tax.isVehicleGroupSpecific || false,
      vehicleGroups: tax.vehicleGroups || [],
      subdivisionOptIn: tax.subdivisionOptIn,
      subdivisionRate: tax.subdivisionRate,
      subdivisionType: tax.subdivisionType,
      subdivisionValue: tax.subdivisionValue
    }
  }) : [];

  let totalSubscriptionAmount = props.bookingData?.insuranceRate + 0;

  totalSubscriptionAmount += (props.bookingData.activeSubscription?.subscriptionAmount || 0) +
    (props.bookingData.activeSubscription?.enrollmentAmount || 0) +
    getRetnalTaxAmount(authUser?.customer?.taxExempted ? [] : taxes, props.bookingData.activeSubscription?.subscriptionAmount || 0, bookingDurationInDays, 1) +
    getRetnalTaxAmount(authUser?.customer?.taxExempted ? [] : taxes, props.bookingData.activeSubscription?.enrollmentAmount || 0, 1, 1)

  const [loadAddons, { loading: addonsLoading, data: addonsData }] = useLazyQuery(GET_AVAILABLE_ADDON_TYPES, {
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    if (props.bookingData.branchId) {
      loadAddons({
        variables: {
          branchId: props.bookingData.branchId,
          startDate: props.bookingData.pickupDateTime,
          endDate: props.bookingData.dropoffDateTime,
          bookingId: props.bookingData.id || "",
          rateTypeName: props.bookingData.rateTypeName,
          businessCustomer: props.bookingData.businessCustomer ? props.bookingData.businessCustomer : authState.user?.businesscustomers?.id
        }
      });
    }
  }, []);

  useEffect(() => {
    if (props.bookingData) {
      let bookingDuration;
      const longTermBooking = isLongTermBooking(props.bookingData);
      if (longTermBooking) {
        switch (props.bookingData.rateTypeName) {
          case BILLING_CYCLE_NAME.WEEKLY:
            if (props.bookingData.isRecurringBilling && props.bookingData.billingFrequency) {
              bookingDuration = 7 * props.bookingData.billingFrequency
            } else {
              bookingDuration = 7
            }
            setBookingDurationInDays(bookingDuration);
            break;
          case BILLING_CYCLE_NAME.MONTHLY:
            if (props.bookingData.isRecurringBilling && props.bookingData.billingFrequency) {
              let idealEndDate = d.fromISO(props.bookingData.pickupDateTime || "").plus({ months: props.bookingData.billingFrequency }).toUTC().toISO();
              bookingDuration = d.fromISO(idealEndDate).diff(d.fromISO(props.bookingData.pickupDateTime), "day").days;
              setBookingDurationInDays(bookingDuration);
            }
            break;
          default:
            break;
        }
      } else {
        bookingDuration = Math.ceil(d.fromISO(props.bookingData.dropoffDateTime || "").diff(d.fromISO(props.bookingData.pickupDateTime || ""), ["days"]).days);
        setBookingDurationInDays(bookingDuration);
      }
    }
  }, [props.bookingData]);

  useEffect(() => {
    let monthlyPricing: ISuggestedPricing = { subscriptionAmount: Infinity, rateTypeName: 'monthly', subscription: "", duration: 0 };
    let weeklyPricing: ISuggestedPricing = { subscriptionAmount: Infinity, rateTypeName: 'weekly', subscription: "", duration: 0 };

    props.selectedVehicle?.subscriptions.forEach(subscription => {
      subscription.pricings.forEach(pricing => {
        if (subscription.subscription.rateTypeName === 'monthly') {
          if (pricing.subscriptionAmount < monthlyPricing.subscriptionAmount) {
            monthlyPricing = {
              subscriptionAmount: pricing.subscriptionAmount,
              rateTypeName: 'monthly',
              subscription: subscription.subscription.id || "",
              duration: pricing.duration
            };
          }
        } else if (subscription.subscription.rateTypeName === 'weekly') {
          if (pricing.subscriptionAmount < weeklyPricing.subscriptionAmount) {
            weeklyPricing = {
              subscriptionAmount: pricing.subscriptionAmount,
              rateTypeName: 'weekly',
              subscription: subscription.subscription.id || "",
              duration: pricing.duration
            };
          }
        }
      });
    });

    const pricing = monthlyPricing.subscriptionAmount < Infinity ? monthlyPricing : weeklyPricing;
    let subscription
    let selectedPricing
    subscription = props.selectedVehicle?.subscriptions.find(sub => sub.subscription.id === pricing.subscription)
    selectedPricing = subscription?.pricings.find(p => p.duration === pricing.duration)
    if (props.bookingData.activeSubscription && props.bookingData.activeSubscription.vehicle === props.selectedVehicle?.id) {
      subscription = props.selectedVehicle?.subscriptions.find(sub => sub.subscription.id === props.bookingData.activeSubscription.subscription)
      selectedPricing = subscription?.pricings.find(pricing => pricing.duration === props.bookingData.activeSubscription.duration)
      setSelectedPlan(subscription)
      setSelectedPricing(selectedPricing)
    } else {
      setSelectedPlan(subscription)
      setSelectedPricing(selectedPricing)
    }

    if (subscription && pricing && props.selectedVehicle?.availableFrom && !props.bookingData.id) {
      const currentBranch = website.branches.find(branch => branch.id === props.bookingData.branchId)
      const availableFromDate = props.selectedVehicle.availableFrom
      const availableTimeMinutes = d.fromISO(availableFromDate).minute
      const roundToMultipleOf15 = Math.ceil(availableTimeMinutes / 15) * 15;
      const minutesToSubtract = d.now().hasSame(d.fromISO(availableFromDate), "day")
      let updateStartDate = d.fromISO(availableFromDate).setZone(currentBranch?.timeZone || "Europe/London").set({ minute: roundToMultipleOf15, millisecond: 0 }).minus({ minutes: minutesToSubtract ? 15 : 0 }).toUTC().toISO()
      const durationKey = subscription.subscription.rateTypeName === "monthly" ? "month" : "week";
      const endDate = d.fromISO(updateStartDate || "").setZone(currentBranch?.timeZone || "Europe/London").plus({ [durationKey]: selectedPricing?.duration }).toUTC().toISO()
      setTimeZone(currentBranch?.timeZone || "")
      const resolvedMinDate = d.max(
        d.fromISO(updateStartDate),
        getMinPickupDateTime(leadTimeSettings, currentBranch?.timeZone)
      );
      setMinDate(resolvedMinDate.minus({ millisecond: 59 }).toUTC().toISO())
      if (d.now().hasSame(d.fromISO(updateStartDate), "day") && currentBranch?.vehiclePreparationTime === 45 && availableTimeMinutes === 45) {
        updateStartDate = d.fromISO(updateStartDate).minus({ minutes: 15 }).toUTC().toISO()
      }
      setStartDate(updateStartDate || "")
      setEndDate(endDate)
      props.onSelectSubscription(
        {
          pricing: selectedPricing,
          subscription: subscription.subscription as any,
          deposit: subscription?.deposit
        },
        props.selectedVehicle as any,
        updateStartDate);
    } else {
      const currentBranch = website.branches.find(branch => branch.id === props.bookingData.branchId)
      const availableFrom = d.fromISO(props.bookingData.pickupDateTime).setZone(currentBranch?.timeZone || "Europe/London").toUTC().toISO()

      setTimeZone(currentBranch?.timeZone || "")
      const resolvedMinDate = d.max(
        d.fromISO(availableFrom),
        getMinPickupDateTime(leadTimeSettings, currentBranch?.timeZone)
      );
      setMinDate(resolvedMinDate.toUTC().toISO())
      setStartDate(resolvedMinDate.toUTC().toISO() || "")
      setEndDate(props.bookingData.dropoffDateTime || "")
      props.onSelectSubscription(
        {
          pricing: selectedPricing,
          subscription: subscription?.subscription as any,
          deposit: subscription?.deposit
        },
        props.selectedVehicle as any,
        resolvedMinDate.toUTC().toISO());
    }

  }, [props.selectedVehicle])

  const getAddonRate = (addon: IAddonRequirementInput, rentalTime: number) => {
    let rate = addon.rate * addon.quantity;
    if (!addon.hasFixedRate && rentalTime) {
      rate = rate * rentalTime;
    }
    return rate;
  };

  return (
    <Grid container item xs={12} spacing={2} >
      <Grid item xs={8}>
        {/* <Grid item xs={12}>
          <Typography style={{ fontSize: 32, textTransform: "capitalize" }} variant='h2'>{`${props.selectedVehicle?.make} ${props.selectedVehicle?.model}`}</Typography>
          <Typography variant={"h3"} style={{ fontSize: 14, color: "#000", marginTop: 8 }}>Available From: {getLocalizedDateFormat(country, props.selectedVehicle?.availableFrom || "", DATE_TYPE.CONDENSED)}</Typography>
          {selectedPlan?.subscription.insurancePolicy && (
            <Typography variant='h3' color='secondary' style={{ fontSize: 14 }}>Included {selectedPlan.subscription.insurancePolicy.name} Insurance</Typography>
          )}
        </Grid> */}
        <Box mt={2}></Box>
        <Grid item xs={12}>
          <div className='carauselContainer' style={{ borderRadius: "0px" }} >
            <Slider className="slickySlider" {...settings}>
              <div className='imageContainer' onClick={() => { }}>
                <img
                  className={"image"}
                  alt="loading.."
                  src={props.selectedVehicle?.imageUrl || `${getVehicleTypeIcon(capitalizeFirstLetter(props.selectedVehicle?.bodyType || ""))}`}
                />
              </div>
              <div className='imageContainer' onClick={() => { }}>
                <img
                  className={"image"}
                  alt="loading.."
                  src={props.selectedVehicle?.imageUrl || `${getVehicleTypeIcon(capitalizeFirstLetter(props.selectedVehicle?.bodyType || ""))}`}
                />
              </div>
            </Slider>
          </div>
        </Grid>
        <Box mt={8}></Box>

        {vehicleDetailsSettings?.showVehicleOnGroup && vehicleDetailsSettings?.categories?.specification?.enabled && (
          <Box className={classes.specBox}>
            {[
              { icon: getFeatureTypeIcon("numberOfDoors"), label: `${props.selectedVehicle?.numberOfDoors} Door`, visibility: vehicleDetailsSettings?.categories?.specification?.fields?.numberOfDoors },
              { icon: getFeatureTypeIcon("numberOfSeats"), label: `${props.selectedVehicle?.numberOfSeats} Seats`, visibility: vehicleDetailsSettings?.categories?.specification?.fields?.numberOfSeats },
              { icon: getFeatureTypeIcon("fuelType"), label: props.selectedVehicle?.fuelType || "", visibility: vehicleDetailsSettings.categories?.specification?.fields?.fuelType },
              { icon: getFeatureTypeIcon("transmission"), label: props.selectedVehicle?.transmission || "", visibility: vehicleDetailsSettings?.categories?.specification?.fields?.transmission },
            ].filter(item => item.visibility && item.label).map((s, i) => (
              <Box
                key={i}
                className={classes.specItem}
              >
                <img src={s.icon} alt={s.label || ""} style={{ width: 16, height: 16 }} />
                <Typography className={classes.specLabel}>{s.label}</Typography>
              </Box>
            ))}

            {vehicleDetailsSettings.categories?.specification?.fields?.color && props.selectedVehicle?.colour && (
              <Box className={classes.specItem}>
                <Box
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: props.selectedVehicle.colour,
                    border: "1px solid #ddd",
                  }}
                />
                <Typography className={classes.specLabel}>
                  {capitalizeFirstLetter(props.selectedVehicle.colour)}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Grid item xs={12}><Box mt={5}></Box></Grid>

        <Grid item xs={12}>
          {vehicleDetailsSettings?.showVehicleOnGroup && vehicleDetailsSettings?.categories && props.selectedVehicle && (
            (vehicleDetailsSettings.categories.interiorFeatures?.enabled && props.selectedVehicle.interiorFeatures?.length > 0) ||
            (vehicleDetailsSettings.categories.exteriorFeatures?.enabled && props.selectedVehicle.exteriorFeatures?.length > 0) ||
            (vehicleDetailsSettings.categories.safetyAndTech?.enabled && props.selectedVehicle.safetyAndTechnicalFeatures?.length > 0) ||
            (vehicleDetailsSettings.categories.otherFeatures?.enabled && props.selectedVehicle.features?.length > 0)
          ) && (
              <>
                <Typography variant="h1" style={{ fontWeight: 500, marginTop: 24, marginBottom: 8 }}>{t("root:features")}</Typography>
                <VehicleFeatures
                  vehicle={props.selectedVehicle!}
                  showInteriorFeatures={vehicleDetailsSettings?.categories?.interiorFeatures?.enabled}
                  showExteriorFeatures={vehicleDetailsSettings?.categories?.exteriorFeatures?.enabled}
                  showSafetyAndTech={vehicleDetailsSettings?.categories?.safetyAndTech?.enabled}
                  showOtherFeatures={vehicleDetailsSettings?.categories?.otherFeatures?.enabled}
                />
              </>
            )}
        </Grid>

        <Grid item xs={12}>
          <Box mt={1}></Box>
          {(selectedPlan?.subscription.insurancePolicy ||
            (selectedPricing?.swapsAllowed && selectedPricing.swapsAllowed > 0) ||
            (selectedPlan?.subscription.addons && selectedPlan.subscription.addons.length > 0)) && (
              <Box style={{ marginTop: 32 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box className={classes.cardContainer}>
                      <Box className={classes.cardHeader}>
                        <Box className={`${classes.iconWrapper} ${classes.iconWrapperPrimary}`}>
                          <DoneIcon style={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </Box>
                        <Typography variant="h3" className={classes.cardTitle}>
                          {t("root:whatsIncluded")}
                        </Typography>
                      </Box>

                      <Box component="ul" className={classes.listContainer}>
                        {selectedPlan?.subscription.insurancePolicy && (
                          <Box component="li" className={classes.listItem}>
                            <Box className={`${classes.listDot} ${classes.listDotPrimary}`} />
                            <Typography variant="body2" className={classes.listText}>
                              {selectedPlan.subscription.insurancePolicy.name}
                            </Typography>
                          </Box>
                        )}
                        {selectedPricing?.swapsAllowed ? (
                          <Box component="li" className={classes.listItem}>
                            <Box className={`${classes.listDot} ${classes.listDotPrimary}`} />
                            <Typography variant="body2" className={classes.listText}>
                              {selectedPricing.swapsAllowed} {t("root:timeVehicleSwap")}
                            </Typography>
                          </Box>
                        ) : ("")}
                        {selectedPlan?.subscription.addons?.map(addon => (
                          <Box key={addon.displayName} component="li" className={classes.listItem}>
                            <Box className={`${classes.listDot} ${classes.listDotPrimary}`} />
                            <Typography variant="body2" className={classes.listText}>
                              {addon.displayName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box className={classes.cardContainerSecondary}>
                      <Box className={classes.cardHeader}>
                        <Box className={`${classes.iconWrapper} ${classes.iconWrapperSecondary}`}>
                          <InfoIcon style={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                        </Box>
                        <Typography variant="h3" className={classes.cardTitle}>
                          {t("root:contractualDetails")}
                        </Typography>
                      </Box>

                      <Box component="ul" className={classes.listContainer}>
                        <Box component="li" className={classes.listItem}>
                          <Box className={`${classes.listDot} ${classes.listDotSecondary}`} />
                          <Typography variant="body2" className={classes.listText}>
                            {t("root:longerSubscribeSaveMore")}
                          </Typography>
                        </Box>
                        <Box component="li" className={classes.listItem}>
                          <Box className={`${classes.listDot} ${classes.listDotSecondary}`} />
                          <Typography variant="body2" className={classes.listText}>
                            {t("root:tailorSubscriptionToNeeds")}
                          </Typography>
                        </Box>
                        <Box component="li" className={classes.listItem}>
                          <Box className={`${classes.listDot} ${classes.listDotSecondary}`} />
                          <Typography variant="body2" className={classes.listText}>
                            {t("root:easyManagementOnline")}
                          </Typography>
                        </Box>
                        <Box component="li" className={classes.listItem}>
                          <Box className={`${classes.listDot} ${classes.listDotSecondary}`} />
                          <Typography variant="body2" className={classes.listText}>
                            {t("root:SwapCarAndCancel")}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
        </Grid>
        <Grid item xs={12}><Box mt={3}></Box></Grid>

        <Grid item xs={12}><Box mt={3}></Box></Grid>
        {
          selectedPlan && selectedPricing && startDate && endDate && (
            <Grid item xs={12}>
              <AddonsSelection
                setBookingData={props.setBookingData}
                bookingData={props.bookingData}
                selectedSubscriptionPlan={selectedPlan}
              />
            </Grid>
          )
        }
      </Grid>
      <Grid item xs={4} spacing={2}>
        <Grid item xs={12}>
          <Box style={{ padding: 20 }}>
            <Typography style={{ fontWeight: 600, fontSize: 32, textTransform: "capitalize", marginBottom: 15 }} variant='h2'>
              {`${props.selectedVehicle?.make} ${props.selectedVehicle?.model}`}
            </Typography>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}><Typography style={{ fontWeight: 400, fontSize: 22 }} variant='h4'>{t("root:customizeYourContract")}</Typography></Grid>
            </Grid>
            <Box mt={2.5}></Box>
            <Grid item xs={12}>{t("root:customize")}
              <InputLabel id="demo-simple-select-contract-plan-label">
                <Typography style={{ fontSize: 16, fontWeight: 400, }} variant='h3'>{t("root:selectPlan")}</Typography>
              </InputLabel>
              <Box mt={0.5}></Box>
              <FormControl fullWidth variant='outlined'>
                <Select
                  labelId="demo-simple-select-contract-plan-label"
                  id="demo-simple-select-contract-plan-label-select"
                  variant='outlined'
                  className={styles.subscription_input_style}
                  value={selectedPlan?.subscription.id || ""}
                  onChange={(event: any) => {
                    const plan = props.selectedVehicle?.subscriptions.find(sub => sub.subscription.id === event.target.value)
                    if (plan) {
                      setSelectedPricing(undefined)
                      setSelectedPlan(plan)
                    }
                  }}
                >
                  {
                    props.selectedVehicle?.subscriptions.map((subscription, index) => (
                      <MenuItem
                        value={subscription.subscription.id}
                        key={subscription.subscription.id}
                      >
                        {subscription.subscription.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Box mt={2}></Box>
              <InputLabel id="demo-simple-select-contract-plan-label">
                <Typography style={{ fontSize: 16, fontWeight: 400 }} variant='h3'>{t("root:selectTenure")}</Typography>
              </InputLabel>
              <Box mt={0.5}></Box>
              <FormControl fullWidth variant='outlined'>
                <Select
                  labelId="demo-simple-select-contract-period-label"
                  id="demo-simple-select-contract-plan-period-select"
                  value={selectedPricing?.duration || ""}
                  className={styles.subscription_input_style}
                  onChange={(event: any) => {
                    const pricing = selectedPlan?.pricings.find(pricing => pricing.duration === event.target.value)
                    if (pricing && selectedPlan) {
                      setSelectedPricing(pricing)
                      props.onSelectSubscription(
                        {
                          pricing,
                          subscription: selectedPlan.subscription as any,
                          deposit: selectedPlan.deposit
                        },
                        props.selectedVehicle as any,
                        props.bookingData.pickupDateTime || startDate
                      );
                    }
                  }}

                >
                  {
                    selectedPlan?.pricings.map((pricing, index) => (
                      <MenuItem
                        value={pricing.duration}
                        key={index}
                      >
                        {`${pricing.duration} ${selectedPlan.subscription.rateTypeName === "monthly" ? t("root:months") : t("root:weeks")} (${toCurrency(pricing.subscriptionAmount, currency, locale)}/${selectedPlan.subscription.rateTypeName === "monthly" ? t("root:month") : t("root:week")})`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {/* <Box mt={2}></Box>
              <InputLabel id="demo-simple-select-contract-plan-label">
                <Typography style={{ fontSize: 16, fontWeight: 400 }} variant='h3'>Select Mileage</Typography>
              </InputLabel>
              <Box mt={0.5}></Box>
              <FormControl fullWidth variant='outlined'>
                <Select
                  labelId="demo-simple-select-contract-mileage-label"
                  id="demo-simple-select-contract-plan-mileage-select"
                  value={selectedPricing?.duration || ""}
                  className={styles.subscription_input_style}
                  onChange={(event: any) => {
                    const pricing = selectedPlan?.pricings.find(pricing => pricing.duration === event.target.value)
                    if (pricing && selectedPlan) {
                      setSelectedPricing(pricing)
                      props.onSelectSubscription(
                        {
                          pricing,
                          subscription: selectedPlan.subscription as any,
                          deposit: selectedPlan.deposit
                        },
                        props.selectedVehicle as any,
                        props.bookingData.pickupDateTime || startDate
                      );
                    }
                  }}

                >
                  {
                    selectedPlan?.pricings.map((pricing, index) => (
                      <MenuItem
                        value={pricing.duration}
                        key={index}
                      >
                        {`${pricing.duration} ${selectedPlan.subscription.rateTypeName === "monthly" ? "Month's" : "Week's"} (${toCurrency(pricing.subscriptionAmount, currency, locale)}/${selectedPlan.subscription.rateTypeName === "monthly" ? "Month" : "Week"})`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl> */}
            </Grid>
            <Box mt={2}></Box>
            <Grid item xs={12}>
              <InputLabel>
                <Typography variant='h3' style={{ fontSize: 16, fontWeight: 400 }}>{t("root:tentativeStartDate")}</Typography>
              </InputLabel>
              <Box mt={1}></Box>

              <Grid item xs={12}>
                <SimpleDateTimePicker
                  classId="flatpickr-container"
                  date={startDate}
                  handleChange={(date: IDateTime) => {
                    if (date && date.date) {
                      setStartDate("")
                      if (selectedPricing && selectedPlan) {
                        setStartDate(d.fromISO(date.date).toUTC().toISO())
                        props.onSelectSubscription(
                          {
                            pricing: selectedPricing,
                            subscription: selectedPlan.subscription as any,
                            deposit: selectedPlan.deposit
                          },
                          props.selectedVehicle as any,
                          d.fromISO(date.date).toUTC().toISO()
                        );
                      }
                    }
                  }}
                  required={true}
                  name={"startDate"}
                  dateTitle={t("root:selectDate")}
                  timeTitle={t("root:selectTime")}
                  minDate={minDate}
                  maxDate={maxPickupDateTime?.toUTC().toISO()}
                  timeZone={timeZone}
                />
              </Grid>
            </Grid>
            <Box mt={3}></Box>
            {
              selectedPlan && selectedPricing && (
                <>
                  <Grid item xs={12}><Typography style={{ fontWeight: 400, fontSize: 24 }} variant='h4'>{t("root:contractSummary")}</Typography></Grid>
                  <Box mt={3}></Box>
                  <Grid item xs={12} container>
                    <Typography style={{ fontSize: 16 }} variant={"h5"}>{`${isContractHire ? t('root:contractHire') : t('root:subscription')} ${t("root:charges")}`}</Typography>
                  </Grid>
                  <Box mt={3}></Box>
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={12} container justify={"flex-end"} spacing={1}>
                      <Grid item xs={12} container justify={"space-between"}>
                        <Typography variant={"h6"} style={{ fontWeight: 600, textTransform: 'capitalize' }}>{props.bookingData.activeSubscription.rateTypeName}</Typography>
                        <Typography variant={"caption"} style={{ fontWeight: 600 }}>{toCurrency(selectedPricing.subscriptionAmount, currency, locale)}</Typography>
                      </Grid>
                      <Grid item xs={12} container justify={"flex-end"}>
                        <Typography variant='caption'>
                          <IconButton
                            style={{
                              padding: 0,
                              paddingRight: 10
                            }}
                          >
                            <Tooltip
                              title={
                                <React.Fragment>
                                  {
                                    getTaxDescription(taxes, currency, locale).length > 0 &&
                                    getTaxDescription(taxes, currency, locale).map(title => {
                                      return (
                                        <>
                                          <span>({title})</span><br></br>
                                        </>
                                      )
                                    })
                                  }
                                </React.Fragment>
                              }
                              arrow
                              placement={"top"}
                            >
                              <InfoIcon />
                            </Tooltip>
                          </IconButton>
                          {getLocalizedTaxSyntex(country || "")} : {toCurrency(getRetnalTaxAmount(authUser?.customer?.taxExempted ? [] : taxes, props.bookingData?.activeSubscription?.subscriptionAmount || 0, bookingDurationInDays, 1), currency, locale)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} container justify={"space-between"}>
                        <Typography variant={"h6"} style={{ fontWeight: 600 }}>{`${isContractHire ? t('root:initialRentalPayment') : t('root:enrollmentFees')}`}</Typography>
                        <Typography variant={"caption"} style={{ fontWeight: 600 }}>{toCurrency(selectedPricing.enrollmentAmount, currency, locale)}</Typography>
                      </Grid>

                      <Grid item xs={12} container justify={"flex-end"}>
                        <Typography variant='caption'>
                          <IconButton
                            style={{
                              padding: 0,
                              paddingRight: 10
                            }}
                          >
                            <Tooltip
                              title={
                                <React.Fragment>
                                  {
                                    getTaxDescription(taxes, currency, locale).length > 0 &&
                                    getTaxDescription(taxes, currency, locale).map(title => {
                                      return (
                                        <>
                                          <span>({title})</span><br></br>
                                        </>
                                      )
                                    })
                                  }
                                </React.Fragment>
                              }
                              arrow
                              placement={"top"}
                            >
                              <InfoIcon />
                            </Tooltip>
                          </IconButton>
                          {getLocalizedTaxSyntex(country || "")} : {toCurrency(getRetnalTaxAmount(authUser?.customer?.taxExempted ? [] : taxes, props.bookingData?.activeSubscription?.enrollmentAmount || 0, 1, 1), currency, locale)}
                        </Typography>
                      </Grid>


                    </Grid>
                    {
                      props.bookingData.addonRequirements.length > 0 && (
                        <Grid item xs={12} container>
                          <Typography style={{ fontSize: 16 }} variant={"h5"}>{t("root:addons")}</Typography>
                        </Grid>
                      )
                    }
                    {
                      props.bookingData.addonRequirements.map((addon, index) => {
                        let taxAmount = 0;
                        if (!authUser?.customer?.taxExempted) {
                          if (addon.hasFixedRate) {
                            if (addon.tax && addon.tax.value) {
                              taxAmount = Math.round(addon.rate * addon.tax.value / 100) * addon.quantity;
                            }
                          } else {
                            if (addon.tax && addon.tax.value) {
                              taxAmount = Math.round(addon.rate * addon.tax.value / 100) * addon.quantity * 1;
                            }
                          }
                        }
                        totalSubscriptionAmount += getAddonRate(addon, 1) + taxAmount
                        return (
                          <Grid container item xs={12} spacing={1}>
                            <Grid container item xs={6}>
                              <Typography variant='h4'>{addon.displayName ? addon.displayName : addon.name}</Typography>
                            </Grid>
                            <Grid item xs={6} container justify={"flex-end"} spacing={1}>
                              <Typography variant="h4">{toCurrency(getAddonRate(addon, 1) + taxAmount, currency, locale)}</Typography>
                            </Grid>
                            <Grid item xs={6} container>
                              <Typography variant='caption'>{t("root:quantity")}: {addon.quantity}</Typography>
                            </Grid>
                            <Grid item xs={6} container justify='flex-end'>
                              <Typography variant="caption">{addon.quantity} x {addon.hasFixedRate ? toCurrency(addon.rate, currency, locale) : `(${1} ${RentalTimeText[props.bookingData.rateTypeName as RATE_TYPES]}${1 > 1 ? "s" : ""} x ${toCurrency(addon.rate, currency, locale)})`}</Typography>
                            </Grid>
                            <Grid item xs={12} container justify='flex-end' alignItems='center'>
                              {
                                addon.tax && addon.tax.title && addon.tax.value && (
                                  <IconButton
                                    style={{
                                      padding: 0,
                                      paddingRight: 10
                                    }}
                                  >
                                    <Tooltip
                                      title={
                                        <>
                                          <Typography>({`${addon.tax.title} ${addon.tax.value}%`})</Typography>
                                        </>
                                      }
                                      arrow
                                      placement={"right"}
                                    >
                                      <InfoIcon />
                                    </Tooltip>
                                  </IconButton>
                                )
                              }
                              {getLocalizedTaxSyntex(country || "")}  : {toCurrency(taxAmount, currency, locale)}
                            </Grid>
                          </Grid>
                        )
                      })
                    }
                    {(() => {
                      if (props.bookingData.insurancePolicy) {
                        if (props.bookingData.insurancePercentageRate) {
                          if (!props.bookingData.insuranceRate && props.bookingData.insurancePercentageRate) {
                            const insuranceRate = Math.round((props.bookingData.insurancePercentageRate / 100) * (selectedPricing.subscriptionAmount || 0))
                            totalSubscriptionAmount += insuranceRate
                          } else if (props.bookingData.insuranceRate) {
                            totalSubscriptionAmount += props.bookingData.insuranceRate
                          }

                          return (
                            <>
                              <Grid item xs={12} container>
                                <Typography style={{ fontSize: 16 }} variant={"h5"}>{t("root:insurance")}</Typography>
                              </Grid>
                              <Grid container item xs={12} spacing={1}>
                                <Grid container item xs={6}>
                                  <Typography variant='h4'>{props.bookingData.insuranceName}</Typography>
                                </Grid>
                                <Grid item xs={6} container justify={"flex-end"} spacing={1}>
                                  <Typography variant="h4">{toCurrency((props.bookingData.insurancePercentageRate * props.bookingData.activeSubscription?.subscriptionAmount) / 100, currency, locale)}</Typography>
                                </Grid>

                                <Grid container item xs={6}>
                                  <Typography variant='caption'>{`(${props.bookingData.insurancePercentageRate}% of subscription price)`}</Typography>
                                </Grid>
                                {/* <Grid item xs={6} container justify={"flex-end"} spacing={1}>
                                    <Typography variant="h4">{toCurrency(props.bookingData.insuranceRate * 1 * (1), currency, locale)}</Typography>
                                  </Grid> */}
                              </Grid>
                            </>
                          )

                        } else {

                          totalSubscriptionAmount += props.bookingData.insuranceRate || 0
                          return (
                            <>
                              <Grid item xs={12} container>
                                <Typography style={{ fontSize: 16 }} variant={"h5"}>{t("root:insurance")}</Typography>
                              </Grid>
                              <Grid container item xs={12} spacing={1}>
                                <Grid container item xs={6}>
                                  <Typography variant='h4'>{props.bookingData.insuranceName}</Typography>
                                </Grid>
                                <Grid item xs={6} container justify={"flex-end"} spacing={1}>
                                  <Typography variant="h4">{toCurrency(props.bookingData.insuranceRate * 1 * (1), currency, locale)}</Typography>
                                </Grid>
                              </Grid>
                            </>
                          )
                        }
                      }

                    })()}

                    <NuvvenDivider noMargin />
                    <Grid item xs={12} container spacing={1}>
                      <Grid item xs={6} container>
                        <Typography style={{ fontSize: 16 }} variant={"h5"}>{t("root:total")}</Typography>
                      </Grid>
                      <Grid item xs={6} container justify={"flex-end"}>
                        <Typography variant={"h4"}>{toCurrency(totalSubscriptionAmount, currency, locale)}</Typography>
                      </Grid>
                    </Grid>
                    {
                      selectedPlan?.deposit && (
                        <>
                          <NuvvenDivider noMargin />
                          <Grid container item xs={12}>
                            <Grid container item xs={6}>
                              <Typography style={{ fontSize: 16, fontWeight: 600 }} variant={"h5"}>{t("root:deposit")}</Typography>
                            </Grid>
                            <Grid container item xs={6} justify={"flex-end"} spacing={1}>
                              <Typography variant="h4">{toCurrency(selectedPlan.deposit, currency, locale)}</Typography>
                            </Grid>
                          </Grid>
                        </>
                      )
                    }
                  </Grid>
                  <Box mt={2.5}></Box>
                  <Grid item xs={12}>
                    <Button
                      className={styles.next_step_btn}
                      onClick={() => props.onSubmit(props.bookingData)}
                      fullWidth
                    >
                      {t("root:continueToPersonalDetails")}
                    </Button>
                  </Grid>
                </>
              )
            }
          </Box>
        </Grid>
      </Grid >
    </Grid >
  )
}

export default VehicleDetail